import React from "react";
import { EmptyState, Icon } from "./registry";
/* ============================================================
   Fence Works — Financing (dedicated page)
   Per-contractor Wisetack: connect YOUR account once, then the
   platform pipes everything — quote "Finance monthly" + the hub's
   "Apply for monthly financing" generate real application links
   from your merchant account, and the webhook flags quotes
   financed on approval.
   ============================================================ */

function FinancingPage({ store, go }: any) {
  const DB: any = window.VFP_DB;
  const s = store.settings || {};
  const fin0 = Object.assign({}, (window.VFP.DEFAULT_SETTINGS || {}).financing, s.financing);
  const [fin, setFin] = React.useState<any>(fin0);
  const [apiKey, setApiKey] = React.useState<any>("");
  const [merchantId, setMerchantId] = React.useState<any>((s.integrations && s.integrations.wisetack && s.integrations.wisetack.merchantId) || "");
  const [busy, setBusy] = React.useState<any>(false);
  const [saved, setSaved] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [activity, setActivity] = React.useState<any>(null);

  const conn = (s.integrations && s.integrations.wisetack) || {};
  const connected = !!conn.connected;
  const liveService = !!(DB && DB.apiBase && DB.apiBase());

  React.useEffect(() => {
    if (!DB || !DB.financingActivity) return;
    DB.financingActivity().then(setActivity).catch(() => setActivity([]));
  }, []);

  async function connect() {
    setErr("");
    if (!apiKey.trim()) { setErr("Paste your Wisetack API key first."); return; }
    setBusy(true);
    try {
      if (liveService && DB.connectIntegration) {
        await DB.connectIntegration("wisetack", { apiKey: apiKey.trim(), merchantId: merchantId.trim() });
      }
      const integrations = { ...(s.integrations || {}), wisetack: { connected: true, connectedAt: Date.now(), merchantId: merchantId.trim() } };
      store.setSettings({ integrations });
      setApiKey("");
      store.addActivity("Connected Wisetack financing");
    } catch (e: any) { setErr(String((e && e.message) || e)); }
    finally { setBusy(false); }
  }
  async function disconnect() {
    setBusy(true); setErr("");
    try {
      // only flip local state once the server actually dropped the key
      if (liveService && DB.disconnectIntegration) await DB.disconnectIntegration("wisetack");
      const integrations = { ...(s.integrations || {}), wisetack: { connected: false } };
      store.setSettings({ integrations });
    } catch (e: any) { setErr("Couldn't disconnect Wisetack: " + String((e && e.message) || e)); }
    finally { setBusy(false); }
  }
  function saveDisplay() {
    store.setSettings({ financing: { ...fin, partner: fin.partner || "Wisetack" } });
    setSaved(true); setTimeout(() => setSaved(false), 1800);
  }

  const monthly = window.VFP.monthlyPayment(20000, fin.apr, fin.termMonths);

  /* One connected stepper instead of three floating cards: a rail ties the
     steps together so the sequence reads at a glance, each step says where it
     stands, and the action drops onto its own row instead of being squeezed
     against the title on a phone. */
  const Step = ({ n, title, state, note, children, action }: any) => (
    <li className={"fin-step is-" + state}>
      <div className="fin-step-rail" aria-hidden="true">
        <span className="fin-step-dot">{state === "done" ? <Icon name="check" size={15} /> : n}</span>
      </div>
      <div className="fin-step-body">
        <div className="fin-step-head">
          <h2 className="fin-step-title">{title}</h2>
          {note && <span className="fin-step-note">{note}</span>}
        </div>
        {children}
        {action && <div className="fin-step-action">{action}</div>}
      </div>
    </li>
  );

  return (
    <div className="page fade-up" style={{ maxWidth: 980 }}>
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Financing</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Offer monthly payments on every quote from <b style={{ color: "var(--fg-2)" }}>your own Wisetack account</b> — bigger jobs close more often when customers see a payment instead of a lump sum.</p>
        </div>
        <span className={"chip " + (connected ? "green" : "")} style={{ alignSelf: "flex-start", fontSize: 11.5 }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: connected ? "var(--green)" : "var(--fg-faint)" }} />
          {connected ? "Wisetack connected" + (conn.merchantId ? " · " + conn.merchantId : "") : "Not connected"}
        </span>
      </div>

      <ol className="card fin-steps">
        <Step n={1} title="Get a Wisetack account" state={connected ? "done" : "todo"} note={connected ? "Done" : "At wisetack.com"}>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Sign up (or log in) at <a href="https://www.wisetack.com/" target="_blank" rel="noopener" style={{ color: "var(--teal)" }}>wisetack.com</a> — it's your merchant account and your relationship. Ask them for <b style={{ color: "var(--fg-2)" }}>API access</b> for your software integration; they'll issue an API key and your merchant ID.
          </p>
          {!connected && <div className="fin-step-action"><a className="btn btn-outline btn-sm" href="https://www.wisetack.com/" target="_blank" rel="noopener"><Icon name="send" size={13} />Open wisetack.com</a></div>}
        </Step>

        <Step n={2} title="Connect it here" state={connected ? "done" : "now"} note={connected ? "Connected" : "You're here"}
          action={connected ? <button className="btn btn-ghost btn-sm" disabled={busy} onClick={disconnect}>Disconnect</button> : null}>
          {connected ? (
            <div>
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>Connected{conn.connectedAt ? " on " + new Date(conn.connectedAt).toLocaleDateString() : ""}. Your API key is stored in the encrypted vault — it never touches the browser again.</p>
              {err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: "6px 0 0" }}>{err}</p>}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
              <div className="row gap-10" style={{ flexWrap: "wrap" }}>
                <div style={{ flex: 2, minWidth: 220 }}><label className="field-label">Wisetack API key</label><input className="input" type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="wt_…" autoComplete="off" /></div>
                <div style={{ flex: 1, minWidth: 160 }}><label className="field-label">Merchant ID <span className="faint">optional</span></label><input className="input" value={merchantId} onChange={e => setMerchantId(e.target.value)} placeholder="mer_…" /></div>
              </div>
              <div className="row gap-8">
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={connect}><Icon name="lock" size={13} />{busy ? "Connecting…" : "Connect Wisetack"}</button>
                {err && <span style={{ color: "var(--red)", fontSize: 12.5 }}>{err}</span>}
              </div>
              <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>The key goes straight to the encrypted vault on our server{liveService ? "" : " (demo mode: stored locally until the live service is configured)"}.</p>
            </div>
          )}
        </Step>

        <Step n={3} title="Done — we pipe the rest" state={connected ? "done" : "later"} note={connected ? "Live" : "Automatic"}>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }}>Nothing else to do. Once connected:</p>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 5 }}>
            <li className="muted" style={{ fontSize: 12.5 }}><b style={{ color: "var(--fg-2)" }}>Quotes</b> show "Finance monthly" — clicking it creates a real Wisetack application for the job amount + customer and opens their prequalification page.</li>
            <li className="muted" style={{ fontSize: 12.5 }}><b style={{ color: "var(--fg-2)" }}>The customer hub</b> offers "Apply for monthly financing" right next to Pay deposit.</li>
            <li className="muted" style={{ fontSize: 12.5 }}><b style={{ color: "var(--fg-2)" }}>Approvals flow back</b> automatically — Wisetack notifies us and the quote is flagged financed.</li>
          </ul>
        </Step>
      </ol>

      {/* display settings: what the customer sees on quotes */}
      <div className="card pad" style={{ marginBottom: 12 }}>
        <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 12 }}><Icon name="dollar" size={14} />Shown on quotes</div>
        <label className="toggle-row" style={{ marginBottom: connected ? 12 : 6, opacity: connected ? 1 : 0.55, cursor: connected ? "pointer" : "not-allowed" }}
          onClick={() => { if (connected) setFin((f: any) => ({ ...f, enabled: !f.enabled })); }}>
          <span className={"sw" + (connected && fin.enabled ? " on" : "")}><span className="knob" /></span>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>Show financing on quotes &amp; the customer hub</span>
        </label>
        {!connected && <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
          <Icon name="lock" size={11} /> Locked off until your Wisetack account is connected — customers never see a financing button that would just error.
        </p>}
        <div className="row gap-12" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ width: 110 }}><label className="field-label">APR %</label><input className="input tnum" type="number" step="0.1" value={fin.apr} onChange={e => setFin((f: any) => ({ ...f, apr: +e.target.value || 0 }))} /></div>
          <div style={{ width: 110 }}><label className="field-label">Term (mo)</label><input className="input tnum" type="number" value={fin.termMonths} onChange={e => setFin((f: any) => ({ ...f, termMonths: +e.target.value || 0 }))} /></div>
          <span className="muted" style={{ fontSize: 12.5, paddingBottom: 9 }}>Example: a $20,000 job shows ≈ <b style={{ color: "var(--teal)" }}>{window.VFP.money(monthly)}/mo</b></span>
        </div>
        <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>APR/term only drive the estimated monthly payment shown to the customer — actual offers come from Wisetack at application time.</p>
        <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={saveDisplay}>{saved ? <><Icon name="check" size={14} />Saved</> : "Save"}</button>
      </div>

      {/* recent financing activity (events the pipe writes) */}
      <div className="card pad">
        <div className="row gap-8 kicker" style={{ marginBottom: 12 }}><Icon name="activity" size={14} />Recent financing activity</div>
        {activity == null
          ? <p className="muted" style={{ fontSize: 13 }}>Loading…</p>
          : activity.length === 0
            ? <EmptyState icon="dollar" title="No financing activity yet" sub="Applications and approvals show up here once customers start financing their quotes." />
            : <div style={{ display: "grid", gap: 6 }}>
                {activity.map((e: any, i: number) => (
                  <div key={i} className="row between" style={{ padding: "8px 10px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, fontSize: 12.5 }}>
                    <span>{e.type === "financing.offered" ? "Application link created" : "Status: " + (e.outcome || "update")}{e.payload && e.payload.quote_id ? " · quote #" + String(e.payload.quote_id).slice(-6).toUpperCase() : ""}</span>
                    <span className="muted">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                  </div>
                ))}
              </div>}
      </div>
    </div>
  );
}

Object.assign(window, { FinancingPage });

export { FinancingPage };
